import { computed, reactive, ref } from '@vue/composition-api'
import { debounce } from 'lodash'
import { errorNotification } from '@/utils/toast-notifications'

const settingsSnapshotTemp = {
  minimum_amount: undefined,
  tax_amount: undefined,
  open_meter_amount: undefined,
  admin_commission: undefined,
  estimates: {
    rate: undefined,
    value: undefined,
  },
  arrival: {
    amount_per_minute: undefined,
    free_waiting_time: undefined,
    maximum_time_threshold: undefined,
  },
  upfront_estimates: {
    active: undefined,
    minimum_distance_threshold: undefined,
    distance: {
      rate: undefined,
      minimum: undefined,
      maximum: undefined,
    },
    time: {
      rate: undefined,
      minimum: undefined,
      maximum: undefined,
    },
  },
  drop_zones: {},
  peak_factors: [],
  time_slicings: [],
  distance_slicings: [],
}
export default function usePricingOverrideForm(ctx, props) {
  const pricing = ref({
    label: undefined, // required
    effective_date_time: undefined,
    zone: props.isNew ? [] : undefined, // array for CREATE, single value for UPDATE
    service_class: props.isNew ? [] : undefined, // array for CREATE, single value for UPDATE
    priority: undefined, // required
    group: undefined, // required
    settings: { ...settingsSnapshotTemp },
  })
  const rateOptions = [
    {
      text: 'Percentage',
      value: 'percentage',
    },
    {
      text: 'Fixed',
      value: 'fixed',
    },
  ]
  const pricingOverrideID = ctx.root.$route.params.id
  const zones = reactive({})
  const fetchingZones = ref(false)
  const zonesNames = ref([])
  const formZonesOptions = ref([])
  const formServicesOptions = ref([])
  const fetchZones = async () => {
    try {
      fetchingZones.value = true
      const res = await ctx.root.$store.dispatch('zones/fetchZones')
      formZonesOptions.value = res
      zonesNames.value = [...new Set(res.map(el => `${el.country}::${el.city}`))]
      zonesNames.value.forEach(name => {
        zones[name] = null
      })
    } catch (error) {
      console.log(error)
    } finally {
      fetchingZones.value = false
    }
  }

  const fetchServices = async () => {
    try {
      const res = await ctx.root.$store.dispatch('services/fetchServices')
      formServicesOptions.value = res
    } catch (error) {
      console.log(error)
    }
  }
  const fetchPricingOverride = async () => {
    const res = await ctx.root.$store.dispatch('pricing-override/getPricingOverride', pricingOverrideID)
    const { data } = res.data
    pricing.value = { ...res.data.data }

    pricing.value.settings = { ...settingsSnapshotTemp, ...data.settings }

    // Handle API response - load data as received from API
    if (data.zone !== undefined) {
      pricing.value.zone = data.zone
    }

    if (data.service_class !== undefined) {
      pricing.value.service_class = data.service_class
    }
  }

  const groupsList = ref([])

  const fetchGroups = debounce(async (search, loading) => {
    if (!search) return
    loading(true)
    const res = await ctx.root.$store.dispatch('groups/fetchGroups', { name: search })
    groupsList.value = res.filter(group => group.locked)

    loading(false)
  }, 1000)

  const distanceLabeling = computed(() => (pricing.value.settings.upfront_estimates.distance?.rate === 'percentage' ? '(%)' : '(KM)'))
  const timeLabeling = computed(() => (pricing.value.settings.upfront_estimates.time?.rate === 'percentage' ? '(%)' : '(Minute)'))
  const fetching = ref(false)
  const initFetch = async () => {
    fetching.value = true
    await Promise.all([
      fetchZones(),
      fetchServices(),
    ])
    if (pricingOverrideID) {
      await fetchPricingOverride()
    }
    fetching.value = false
  }
  const isValidValue = val => {
    if (val === 0 || val === false || val) return true
    return false
  }
  const submitting = ref(false)
  const submitHandler = async () => {
    if (!pricing.value.label) {
      errorNotification('The label field is mandetory')
      return
    }
    if (!pricing.value.priority) {
      errorNotification('The priority field is mandetory')
      return
    }
    // Services are now independent of zones, so no validation needed here
    if (!pricing.value.group) {
      errorNotification('The group field is mandetory')
      return
    }

    const temp = {
      label: pricing.value.label,
      group: pricing.value.group,
      priority: pricing.value.priority,
      settings: {
        estimates: {},
        arrival: {},
        upfront_estimates: {},
      },
    }
    if (props.isNew) {
      // For CREATE: send arrays with plural keys
      if (pricing.value.zone && pricing.value.zone.length > 0) {
        temp.zones = pricing.value.zone
      }
      if (pricing.value.service_class && pricing.value.service_class.length > 0) {
        temp.service_classes = pricing.value.service_class
      }
    } else {
      // For UPDATE: send singular keys as received
      if (pricing.value.zone !== undefined) {
        temp.zone = pricing.value.zone
      }
      if (pricing.value.service_class !== undefined) {
        temp.service_class = pricing.value.service_class
      }
    }
    const settingsSnapshot = { ...pricing.value.settings }
    if (isValidValue(pricing.value.effective_date_time)) temp.effective_date_time = pricing.value.effective_date_time

    if (isValidValue(settingsSnapshot.minimum_amount)) temp.settings.minimum_amount = settingsSnapshot.minimum_amount
    if (isValidValue(settingsSnapshot.tax_amount)) temp.settings.tax_amount = settingsSnapshot.tax_amount
    if (isValidValue(settingsSnapshot.open_meter_amount)) temp.settings.open_meter_amount = settingsSnapshot.open_meter_amount
    if (isValidValue(settingsSnapshot.admin_commission)) temp.settings.admin_commission = settingsSnapshot.admin_commission

    if (isValidValue(settingsSnapshot.estimates.rate)) temp.settings.estimates.rate = settingsSnapshot.estimates.rate
    if (isValidValue(settingsSnapshot.estimates.value)) temp.settings.estimates.value = settingsSnapshot.estimates.value

    if (isValidValue(settingsSnapshot.arrival.amount_per_minute)) temp.settings.arrival.amount_per_minute = settingsSnapshot.arrival.amount_per_minute
    if (isValidValue(settingsSnapshot.arrival.free_waiting_time)) temp.settings.arrival.free_waiting_time = settingsSnapshot.arrival.free_waiting_time
    if (isValidValue(settingsSnapshot.arrival.maximum_time_threshold)) temp.settings.arrival.maximum_time_threshold = settingsSnapshot.arrival.maximum_time_threshold

    if (isValidValue(settingsSnapshot.upfront_estimates.active)) temp.settings.upfront_estimates.active = settingsSnapshot.upfront_estimates.active
    if (isValidValue(settingsSnapshot.upfront_estimates.minimum_distance_threshold)) temp.settings.upfront_estimates.minimum_distance_threshold = settingsSnapshot.upfront_estimates.minimum_distance_threshold
    if (isValidValue(settingsSnapshot.upfront_estimates.distance.rate)) temp.settings.upfront_estimates.distance.rate = settingsSnapshot.upfront_estimates.distance.rate
    if (isValidValue(settingsSnapshot.upfront_estimates.distance.minimum)) temp.settings.upfront_estimates.distance.minimum = settingsSnapshot.upfront_estimates.distance.minimum
    if (isValidValue(settingsSnapshot.upfront_estimates.distance.maximum)) temp.settings.upfront_estimates.distance.maximum = settingsSnapshot.upfront_estimates.distance.maximum
    if (isValidValue(settingsSnapshot.upfront_estimates.time.rate)) temp.settings.upfront_estimates.time.rate = settingsSnapshot.upfront_estimates.time.rate
    if (isValidValue(settingsSnapshot.upfront_estimates.time.minimum)) temp.settings.upfront_estimates.time.minimum = settingsSnapshot.upfront_estimates.time.minimum
    if (isValidValue(settingsSnapshot.upfront_estimates.time.maximum)) temp.settings.upfront_estimates.time.maximum = settingsSnapshot.upfront_estimates.time.maximum

    if (!Object.keys(temp.settings.estimates).length) delete temp.settings.estimates
    if (!Object.keys(temp.settings.arrival).length) delete temp.settings.arrival
    if (!Object.keys(temp.settings.upfront_estimates).length) delete temp.settings.upfront_estimates

    if (Object.keys(pricing.value.settings.drop_zones).length) temp.settings.drop_zones = pricing.value.settings.drop_zones
    if (pricing.value.settings.peak_factors.length) temp.settings.peak_factors = pricing.value.settings.peak_factors
    if (pricing.value.settings.time_slicings.length) temp.settings.time_slicings = pricing.value.settings.time_slicings
    if (pricing.value.settings.distance_slicings.length) temp.settings.distance_slicings = pricing.value.settings.distance_slicings

    // if (!Object.keys(temp.settings).length) delete temp.settings
    try {
      submitting.value = true
      if (props.isNew) {
        await ctx.root.$store.dispatch('pricing-override/postPricingOverride', temp)
      } else {
        await ctx.root.$store.dispatch('pricing-override/editPricingOverride', {
          data: temp,
          id: pricingOverrideID,
        })
      }
      ctx.root.$router.push('/pricing-overrides')
    } catch (err) {
      const errMsg = err?.response?.data?.error?.detail?.message
      errorNotification(errMsg)
      console.log(errMsg)
    } finally {
      submitting.value = false
    }
  }
  const zoneInputHandler = selectedZoneIds => {
    if (props.isNew) {
      pricing.value.zone = selectedZoneIds || []
    } else {
      pricing.value.zone = selectedZoneIds
    }
  }

  const serviceInputHandler = selectedServiceIds => {
    if (props.isNew) {
      pricing.value.service_class = selectedServiceIds || []
    } else {
      pricing.value.service_class = selectedServiceIds
    }
  }
  initFetch()
  return {
    pricing,
    timeLabeling,
    distanceLabeling,
    zoneInputHandler,
    serviceInputHandler,
    zones,
    submitting,
    fetching,
    groupsList,
    submitHandler,
    fetchingZones,
    rateOptions,
    fetchGroups,
    formZonesOptions,
    formServicesOptions,
  }
}
